/**
 * WorkOrderPanel - Right panel with Work Order iframe and notes
 *
 * Features:
 * - Office Online iframe with trust indicator
 * - Word and Excel document embedding support
 * - Safe fallback for non-embeddable file types
 * - Special Instructions and Production Notes
 * - Notes & Communication section
 */

import { useState } from 'react';
import { NoteCard, getOfficeOnlineUrl, getFileType } from './projectUtils';

export default function WorkOrderPanel({
  workOrder,
  workOrderUrl,
  notes,
  newNote,
  onNewNoteChange,
  onSendNote,
  sendingNote,
  onGenerateWorkOrder,
}) {
  const wo = workOrder;
  const [iframeKey, setIframeKey] = useState(Date.now());
  const [iframeLoadedAt, setIframeLoadedAt] = useState(null);
  const [embedError, setEmbedError] = useState(false);

  const embedUrl = getOfficeOnlineUrl(workOrderUrl);
  const fileType = getFileType(workOrderUrl);

  const handleIframeLoad = () => {
    setIframeLoadedAt(new Date());
    setEmbedError(false);
  };

  const handleIframeError = () => {
    setEmbedError(true);
  };

  const refreshIframe = () => {
    setIframeKey(Date.now());
    setIframeLoadedAt(null);
    setEmbedError(false);
  };

  // File type labels for display
  const fileTypeLabels = {
    word: 'Word Document',
    excel: 'Excel Spreadsheet',
    pdf: 'PDF Document',
    unknown: 'Document',
  };

  return (
    <div className="w-2/5 overflow-auto bg-slate-200">
      <div className="p-4 space-y-4">

        {/* Work Order Iframe with Trust Indicator */}
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {/* Header with trust indicator */}
          <div className="px-3 py-2 bg-slate-200 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-700">Work Order</span>
              {workOrderUrl && fileType !== 'unknown' && (
                <span className="text-xs text-gray-500">({fileTypeLabels[fileType]})</span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              {iframeLoadedAt && !embedError && (
                <span className="text-green-600">Loaded at {iframeLoadedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              )}
              {embedUrl && (
                <button
                  onClick={refreshIframe}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                  title="Refresh Work Order"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              )}
              {workOrderUrl && (
                <a
                  href={workOrderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                  title="Open in SharePoint"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              )}
            </div>
          </div>

          {/* Content: iframe, fallback, or no document */}
          {workOrderUrl && embedUrl && !embedError ? (
            // Can embed - show iframe
            <iframe
              key={iframeKey}
              src={embedUrl}
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              className="w-full h-64 border-0"
              title={`Work Order - ${fileTypeLabels[fileType]}`}
              sandbox="allow-scripts allow-same-origin allow-forms"
            />
          ) : workOrderUrl && (!embedUrl || embedError) ? (
            // Has URL but can't embed - show fallback with link
            <div className="p-4 bg-yellow-50 border-b border-yellow-100">
              <p className="text-sm text-yellow-800 mb-3">
                {embedError
                  ? 'Unable to load document preview.'
                  : 'This document type cannot be embedded in the browser.'}
              </p>
              <a
                href={workOrderUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Open in SharePoint
              </a>
            </div>
          ) : (
            // No work order attached
            <div className="h-48 flex flex-col items-center justify-center bg-slate-100 text-gray-500 text-sm gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-600 font-medium">No work order attached</p>
              {onGenerateWorkOrder ? (
                <button
                  onClick={onGenerateWorkOrder}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  Generate Work Order
                </button>
              ) : (
                <a
                  href="https://3dcolor.sharepoint.com/sites/ProjectManagementTeam/Shared%20Documents/Work%20Orders"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-700 underline text-sm"
                >
                  Browse Work Orders
                </a>
              )}
            </div>
          )}
        </section>

        {/* Special Instructions */}
        {(wo.special_instructions || wo.specialInstructions) && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-700 font-medium mb-1">Special Instructions</p>
            <p className="text-sm text-gray-700">{wo.special_instructions || wo.specialInstructions}</p>
          </div>
        )}

        {/* Production Notes */}
        {(wo.production_notes || wo.productionNotes) && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-700 font-medium mb-1">Production Notes</p>
            <p className="text-sm text-gray-700">{wo.production_notes || wo.productionNotes}</p>
          </div>
        )}

        {/* Notes & Communication */}
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-3 py-2 bg-slate-200 border-b border-gray-100 flex items-center justify-between">
            <span className="text-xs font-medium text-gray-700">Notes</span>
            <span className="text-xs text-gray-500">{notes.length}</span>
          </div>
          <div className="p-3 space-y-3">
            {/* New note input */}
            <div className="space-y-2">
              <textarea
                value={newNote}
                onChange={(e) => onNewNoteChange(e.target.value)}
                placeholder="Add a note..."
                rows={2}
                className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent resize-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => onSendNote('general')}
                  disabled={!newNote.trim() || sendingNote}
                  className="px-2 py-1 bg-slate-100 hover:bg-gray-200 disabled:bg-slate-200 disabled:text-gray-500 text-gray-700 rounded text-xs font-medium transition-colors"
                >
                  Add
                </button>
                <button
                  onClick={() => onSendNote('question')}
                  disabled={!newNote.trim() || sendingNote}
                  className="px-2 py-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-200 disabled:text-gray-500 text-gray-900 rounded text-xs font-medium transition-colors"
                >
                  Ask PM
                </button>
              </div>
            </div>

            {/* Notes list */}
            {notes.length === 0 ? (
              <div className="text-center py-4 text-gray-500 text-xs">
                No notes yet
              </div>
            ) : (
              <div className="space-y-2">
                {notes.map((note, i) => (
                  <NoteCard key={note.id || i} note={note} />
                ))}
              </div>
            )}
          </div>
        </section>

      </div>
    </div>
  );
}
